import { ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { Flex } from 'components/Flex';
import { ArrowLink } from 'components/Link';
import { RichText } from 'components/Text/RichText';
import { fontSizesByType } from 'theme/theme';
import { useAmpContext } from 'utils/AmpContext';
import { setupLinkTracker } from 'utils/analytics/setup/setupLinkTracker';

import { TruncatedHtml } from './TruncatedHtml';

const PrintBox = styled(Box)<{ $printDisplay: string }>`
  @media print {
    display: ${(props) => props.$printDisplay};
  }
`;

const Container = styled.div<{ $showAll?: boolean }>`
  ${(props) =>
    props.$showAll
      ? `
    overflow-anchor: none;
  `
      : ''}
`;

type Props = {
  html: string;
  truncatedHtml: string;
  showMoreLinkTitle: string;
  showLessLinkTitle: string;
  renderHiddenLink?: () => ReactNode;
  simple?: boolean;
  eventName?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventProperties?: Record<string, any>;
  alwaysTruncate?: boolean;
  onLinkClick?: (link: HTMLAnchorElement) => void;
};

export function TruncatedHtmlWithLinks({
  html,
  truncatedHtml,
  showMoreLinkTitle,
  showLessLinkTitle,
  renderHiddenLink,
  simple,
  eventName,
  eventProperties,
  alwaysTruncate,
  onLinkClick,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const truncatedRef = useRef<HTMLDivElement>(null);

  const [linksAreTracked, setLinksAreTracked] = useState(false);
  useEffect(() => {
    if (eventProperties && eventName && !linksAreTracked) {
      [ref, truncatedRef].forEach((r) => {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (r && r.current) {
          const linkEls = [...r.current.getElementsByTagName('a')];
          linkEls.forEach((link) => {
            if (
              link.hasAttribute('href') &&
              link.getAttribute('href') !== '#'
            ) {
              setupLinkTracker(eventName, eventProperties)(link);
            }
          });
        }
      });
      setLinksAreTracked(true);
    }
  }, [eventProperties, eventName, linksAreTracked, setLinksAreTracked]);

  useEffect(() => {
    if (!onLinkClick) return;

    // Setup link tracking
    [ref, truncatedRef].forEach((r) => {
      if (!r.current) return;

      [...r.current.getElementsByTagName('a')].forEach((link) => {
        if (link.hasAttribute('href') && link.getAttribute('href') !== '#') {
          link.addEventListener('click', () => onLinkClick(link));
        }
      });
    });

    // Teardown event listeners
    return () => {
      [ref, truncatedRef].forEach((r) => {
        if (!r.current) return;

        [...r.current.getElementsByTagName('a')].forEach((link) =>
          link.removeEventListener('click', () => onLinkClick(link)),
        );
      });
    };
  }, [onLinkClick]);

  const requestIsAmp = useAmpContext();
  if (requestIsAmp) {
    return (
      <RichText
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    );
  }

  return (
    <TruncatedHtml
      html={html}
      truncatedHtml={truncatedHtml}
      alwaysTruncate={alwaysTruncate}
      render={({
        truncated,
        truncatedHtml: truncatedHtmlForRender,
        showAll,
        toggleShowAll,
      }) => (
        <Container $showAll={showAll}>
          <PrintBox
            display={showAll ? 'block' : 'none'}
            $printDisplay="block"
            mb="14px"
          >
            <div ref={ref}>
              <RichText
                dangerouslySetInnerHTML={{
                  __html: html,
                }}
                normalize={!simple}
                simple={simple}
              />
            </div>
          </PrintBox>

          <PrintBox
            display={showAll ? 'none' : 'block'}
            $printDisplay="none"
            mb="14px"
          >
            <div ref={truncatedRef}>
              <RichText
                dangerouslySetInnerHTML={{
                  __html: truncatedHtmlForRender,
                }}
                normalize={!simple}
                simple={simple}
              />
            </div>
          </PrintBox>

          <PrintBox $printDisplay="none">
            {truncated ? (
              <Flex flexWrap="wrap">
                <Box width={[1, 1, 1, 'auto']} fontSize={fontSizesByType.body}>
                  <ArrowLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      toggleShowAll();
                    }}
                    open={showAll}
                  >
                    {showAll ? showLessLinkTitle : showMoreLinkTitle}
                  </ArrowLink>
                </Box>

                {renderHiddenLink && showAll && (
                  <Box
                    width={[1, 1, 1, 'auto']}
                    ml={[0, 0, 0, 'auto']}
                    mt={[4, 4, 4, 0]}
                  >
                    {renderHiddenLink()}
                  </Box>
                )}
              </Flex>
            ) : (
              <Flex>
                {renderHiddenLink && (
                  <Box ml={[0, 0, 0, 'auto']}>{renderHiddenLink()}</Box>
                )}
              </Flex>
            )}
          </PrintBox>
        </Container>
      )}
    />
  );
}
